import type { ReactNode } from 'react'
import type { DataItem } from '../domain/rows'
import { Icon } from './Icon'

interface SectionProps {
  /** Anchor target for the quick-jump navigation. */
  id: string
  title: string
  subtitle?: string
  /** Part of the paid tier, and shown as such. See `src/domain/premium.ts`. */
  premium?: boolean
  children: ReactNode
}

/** One white card in the report. */
export function Section({ id, title, subtitle, premium, children }: SectionProps) {
  return (
    <section className={`card${premium ? ' card--premium' : ''}`} id={id} aria-labelledby={`${id}-title`}>
      <div className="card__header">
        <div>
          <h2 className="card__title" id={`${id}-title`}>
            {title}
          </h2>
          {subtitle && <p className="card__subtitle">{subtitle}</p>}
        </div>
        {premium && <PremiumTag />}
      </div>
      {children}
    </section>
  )
}

/**
 * The open padlock and the word "Premium", in black and yellow.
 *
 * It marks content that is part of the paid report, which right now everybody gets for nothing —
 * so it reads as "unlocked", never as "locked". The open padlock is doing that work: a closed one
 * would say the opposite of what is true, and the whole reason this marking exists is to let a
 * reader see what they are being given.
 */
export function PremiumTag() {
  return (
    <span className="premium-tag">
      <Icon name="unlock" size={13} />
      Premium
    </span>
  )
}

/**
 * A premium part of an otherwise free card.
 *
 * Only the APK card needs this: its expiry date is the first thing anybody checks and is free,
 * while the findings behind it are the paid half. Marking the whole card would claim the date is
 * paid content; marking nothing would hide that the history is the valuable part.
 */
export function PremiumBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="premium-block">
      <div className="premium-block__header">
        <h3 className="data-subheading">{title}</h3>
        <PremiumTag />
      </div>
      {children}
    </div>
  )
}

/** The label/value pairs that make up most of the report. */
export function DataRows({ items }: { items: DataItem[] }) {
  if (items.length === 0) {
    return null
  }

  return (
    <dl className="data-rows">
      {items.map((item, index) => (
        <div
          className={`data-row${item.premium ? ' data-row--premium' : ''}${item.prose ? ' data-row--prose' : ''}`}
          key={`${item.label}-${index}`}
        >
          <dt className="data-row__label">
            {item.label}
            {item.premium && <PremiumTag />}
          </dt>
          <dd className="data-row__value">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function DataSubheading({ children }: { children: ReactNode }) {
  return <h3 className="data-subheading">{children}</h3>
}

/**
 * States, in so many words, that the RDW does not publish a value.
 *
 * The default everywhere else is to drop an absent value's row entirely, which is what keeps a
 * 1975 Beetle from getting six empty cards about WLTP figures. That default is wrong for the
 * handful of things every vehicle visibly has: a car with no registered colour renders a report
 * with no colour in it, and a reader concludes the page is broken rather than that the RDW has a
 * gap.
 *
 * So it is written out per field, as `value ?? <NotRegistered />`, and granted only to fields whose
 * absence a reader would notice. Applying it broadly would put a row of "Niet geregistreerd" on
 * every report for every figure the RDW happens not to carry — most of which nobody was looking
 * for — and would undo the whole point of dropping absent rows.
 *
 * It is also emphatically not the same as a value: it says the registration is silent, not that the
 * answer is "no" or "none".
 */
export function NotRegistered() {
  return <span className="empty-value">Niet geregistreerd</span>
}

const ENERGY_LABEL_COLOURS: Record<string, string> = {
  A: 'rgb(87, 160, 94)',
  B: 'rgb(136, 181, 93)',
  C: 'rgb(199, 212, 91)',
  D: 'rgb(253, 241, 85)',
  E: 'rgb(228, 186, 74)',
  F: 'rgb(200, 118, 57)',
  G: 'rgb(186, 58, 48)',
}

/**
 * The arrow-shaped energy label, in the RDW's own colours. `D` is a pale yellow, so that one row
 * takes dark text rather than the white used everywhere else.
 */
export function EnergyLabel({ value }: { value: string }) {
  const colour = ENERGY_LABEL_COLOURS[value.toUpperCase()]

  if (!colour) {
    return <>{value}</>
  }

  return (
    <span
      className="energy-label"
      style={{ background: colour, color: value.toUpperCase() === 'D' ? '#1c1c1c' : '#fff', textShadow: 'none' }}
    >
      {value.toUpperCase()}
    </span>
  )
}

export function Badge({ tone, children }: { tone: 'positive' | 'warning' | 'negative'; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}
